"use client";
import {
  type Address,
  type BaseTransactionMessage,
  type Instruction,
  type TransactionMessageWithBlockhashLifetime,
  type TransactionMessageWithFeePayer,
  address,
  appendTransactionMessageInstructions,
  compileTransaction,
  createTransactionMessage,
  getTransactionEncoder,
  pipe,
  prependTransactionMessageInstruction,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
} from "@solana/kit";

import { getSolanaRpc } from "./rpc";

type TransactionVersion = "legacy" | 0;
type ReadyTransactionMessage = BaseTransactionMessage &
  TransactionMessageWithFeePayer &
  TransactionMessageWithBlockhashLifetime;

const DEFAULT_COMPUTE_UNIT_LIMIT = 500_000;
const COMPUTE_BUDGET_PROGRAM_ADDRESS = address(
  "ComputeBudget111111111111111111111111111111"
);
const COMPUTE_BUDGET_SET_UNIT_LIMIT = 2;
const COMPUTE_BUDGET_SET_UNIT_PRICE = 3;

export interface BuiltTransaction {
  /** Encoded (unsigned) transaction bytes. */
  bytes: Uint8Array;
  /** Slot at which the blockhash was fetched (stringified). */
  slot: string;
}

export interface BuildTransactionOptions {
  /**
   * Sponsorship currently expects legacy `Transaction` bytes server-side.
   * Self-paid wallet submission can use v0.
   */
  version?: TransactionVersion;
  computeUnitLimit?: number;
  priorityFee?: number;
}

const prependComputeUnitLimit = (
  message: ReadyTransactionMessage,
  units = DEFAULT_COMPUTE_UNIT_LIMIT
) =>
  prependTransactionMessageInstruction(
    {
      programAddress: COMPUTE_BUDGET_PROGRAM_ADDRESS,
      data: encodeSetComputeUnitLimit(units),
    },
    message
  );

const prependComputeUnitPrice = (
  message: ReadyTransactionMessage,
  priorityFee?: number
) =>
  priorityFee === undefined
    ? message
    : prependTransactionMessageInstruction(
        {
          programAddress: COMPUTE_BUDGET_PROGRAM_ADDRESS,
          data: encodeSetComputeUnitPrice(priorityFee),
        },
        message
      );

const encodeSetComputeUnitLimit = (units: number): Uint8Array => {
  const data = new Uint8Array(5);
  data[0] = COMPUTE_BUDGET_SET_UNIT_LIMIT;
  new DataView(data.buffer).setUint32(1, units, true);
  return data;
};

const encodeSetComputeUnitPrice = (microLamports: number): Uint8Array => {
  const data = new Uint8Array(9);
  data[0] = COMPUTE_BUDGET_SET_UNIT_PRICE;
  new DataView(data.buffer).setBigUint64(1, BigInt(microLamports), true);
  return data;
};

/**
 * Assembles an unsigned transaction from rise instruction objects. The fee payer is
 * whatever address we pass in (the wallet itself for self-paid, or the
 * sponsorship-provided payer for sponsored flows). We always fetch a fresh
 * `latestBlockhash` + `slot` pair so the sponsorship service can validate it.
 */
export const buildUnsignedTransaction = async (
  feePayer: Address | string,
  instructions: readonly Instruction[],
  options: BuildTransactionOptions = {}
): Promise<BuiltTransaction> => {
  if (instructions.length === 0) {
    throw new Error("buildUnsignedTransaction: empty instruction list");
  }

  const rpc = getSolanaRpc();
  const blockhash = await rpc
    .getLatestBlockhash({ commitment: "confirmed" })
    .send();

  const payerAddress =
    typeof feePayer === "string" ? address(feePayer) : feePayer;

  const message = pipe(
    createTransactionMessage({ version: options.version ?? 0 }),
    (m) => setTransactionMessageFeePayer(payerAddress, m),
    (m) => setTransactionMessageLifetimeUsingBlockhash(blockhash.value, m),
    (m) => appendTransactionMessageInstructions(instructions, m),
    (m) => prependComputeUnitLimit(m, options.computeUnitLimit),
    (m) => prependComputeUnitPrice(m, options.priorityFee)
  );

  const compiled = compileTransaction(message);
  const bytes = getTransactionEncoder().encode(compiled);

  return {
    bytes: new Uint8Array(bytes),
    slot: blockhash.context.slot.toString(),
  };
};
